"use client"

import { useEffect, useRef } from "react"
import { Lock } from "lucide-react"

import { cn } from "@/lib/utils"
import { trackEvent } from "@/lib/analytics"
import { getAllMonthlyTasks, getCurrentMonthIndex, getMonthName } from "@/data/guide-monthly-tasks"
import { resolveGrassDisplayName } from "@/data/grass-characteristics"

const MONTH_ABBREVS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
]

// Locked rows shown before the "+N more months" line — enough to make the
// schedule feel real without pushing the digest's ask below the fold.
const VISIBLE_LOCKED_ROWS = 4

// Words that open a qualifying clause. A locked row shows the words BEFORE the
// first one of these as its readable title and hides the rest.
const CLAUSE_WORDS = new Set([
  "when", "if", "before", "after", "as", "to", "and", "or", "with", "for",
  "until", "once", "while", "that", "on", "in", "at", "from", "using",
  "unless", "over", "into", "about", "around",
])

// Index 0 and 1 are never a clause break: "Watch for early signs…" must not
// reduce to the single word "Watch".
const MIN_TITLE_WORDS = 2
const MAX_TITLE_WORDS = 6
const DEFAULT_TITLE_WORDS = 3

/**
 * Split a full task sentence into a readable short title and the trailing
 * detail the locked row obscures.
 */
export function splitLockedTask(task: string): { title: string; detail: string } {
  const words = task.trim().split(/\s+/)
  if (words.length <= 2) return { title: task.trim(), detail: "" }

  let boundary = DEFAULT_TITLE_WORDS
  for (let i = MIN_TITLE_WORDS; i < words.length && i < MAX_TITLE_WORDS; i++) {
    const word = words[i].toLowerCase().replace(/[^a-z-]/g, "")
    if (CLAUSE_WORDS.has(word)) {
      boundary = i
      break
    }
  }
  boundary = Math.min(boundary, words.length - 1)

  return {
    title: words.slice(0, boundary).join(" ").replace(/[,;:]$/, ""),
    detail: words.slice(boundary).join(" "),
  }
}

interface LockedMonthsStripProps {
  grassKey: string
  onUnlockClick?: () => void
  className?: string
  /**
   * Render without the outer card chrome. Used inside `ResultCarePlanDigest`,
   * where the save ask sits ABOVE the strip in the same card (1a layout), so a
   * tap scrolls back up to it rather than opening anything new. Still a real
   * tap target: 1,184 uniques/mo saw the bare strip while its click affordance
   * was unreachable (2026-08-26 upsell audit).
   */
  bare?: boolean
  /** Skip the internal lock header line — used when the parent renders its own
      lock line naming what's gated (1a digest layout). */
  hideHeader?: boolean
}

/**
 * Preview of the identified grass's real care plan. The next month is readable,
 * while the rest stays visibly locked until the user saves the plan below.
 */
export function LockedMonthsStrip({
  grassKey,
  onUnlockClick,
  className,
  bare = false,
  hideHeader = false,
}: LockedMonthsStripProps) {
  const canonical = resolveGrassDisplayName(grassKey)
  const allMonths = getAllMonthlyTasks(canonical)

  const shownRef = useRef(false)
  useEffect(() => {
    if (!allMonths || shownRef.current) return
    shownRef.current = true
    trackEvent("locked_plan_shown", { grass_type: canonical })
  }, [allMonths, canonical])

  if (!allMonths) return null

  const currentMonth = getCurrentMonthIndex()
  const upcoming = Array.from({ length: 11 }, (_, i) => {
    const monthIndex = (currentMonth + 1 + i) % 12
    const firstTask =
      allMonths[monthIndex]?.find((t) => t.priority === "high") ?? allMonths[monthIndex]?.[0]
    return { monthIndex, task: firstTask?.task ?? "Seasonal care" }
  })
  const nextMonth = upcoming[0]
  const lockedMonths = upcoming.slice(1)

  const handleClick = () => {
    trackEvent("locked_plan_clicked", { grass_type: canonical })
    // An explicit handler owns the unlock action (e.g. opening the save
    // modal) — don't also scroll the page underneath it.
    if (onUnlockClick) {
      onUnlockClick()
      return
    }
    document
      .getElementById("care-plan-upgrade")
      ?.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  const inner = (
    <>
      {!hideHeader && (
        <div className="flex items-center gap-2 mb-3">
          <Lock className="w-3.5 h-3.5 text-green-700" strokeWidth={2.25} />
          <span className="text-[12px] font-bold uppercase tracking-[0.06em] text-green-700">
            {/* Bare (digest) mode: the card header + CTA button already name the
                grass; repeating it here was the 4th mention in one card. */}
            {bare ? "Your full 12-month plan" : `Your full 12-month ${canonical} plan`}
          </span>
        </div>
      )}
      {nextMonth && (
        <div className="rounded-xl border border-green-100 bg-green-50/60 px-3 py-3 mb-3">
          <p className="text-sm font-semibold text-gray-900 leading-snug">
            <span className="text-[11px] font-bold uppercase tracking-[0.06em] text-green-700">
              Next up &middot; {getMonthName(nextMonth.monthIndex)}:
            </span>{" "}
            {nextMonth.task}
          </p>
        </div>
      )}
      {/* The month name AND a short task title stay readable; only the trailing
          detail blurs. A row of unreadable gray chips reads as decoration, so
          each row has to name a real move the plan makes that month.
          Capped rows + a "+N more" line keep the digest compact. */}
      <div data-testid="locked-months-list" className="space-y-1.5">
        {lockedMonths.slice(0, VISIBLE_LOCKED_ROWS).map(({ monthIndex, task }) => {
          const { title, detail } = splitLockedTask(task)
          return (
            <div
              key={monthIndex}
              className="flex items-center gap-2.5 rounded-lg border border-gray-100 bg-gray-50/70 px-3 py-2"
            >
              <span className="w-9 flex-shrink-0 text-[11px] font-bold text-gray-700">
                {MONTH_ABBREVS[monthIndex]}
              </span>
              <span className="min-w-0 flex-1 truncate text-[12px] leading-snug text-gray-700">
                <span className="font-semibold">{title}</span>
                {detail && (
                  <span
                    data-testid="locked-task-detail"
                    className="ml-1 select-none text-gray-500 blur-[3px] pointer-events-none"
                    aria-hidden="true"
                  >
                    {detail}
                  </span>
                )}
              </span>
              <Lock className="h-3 w-3 flex-shrink-0 text-gray-400" strokeWidth={2.25} />
            </div>
          )
        })}
      </div>
      {/* Standalone card only — inside the digest the ask sits directly below,
          so a pointer line is redundant noise. Copy stays destination-agnostic:
          with an onUnlockClick handler the tap opens the save modal, without
          one it scrolls to the on-page ask. */}
      {!bare && (
        <p className="mt-2.5 text-[12px] font-semibold text-green-700">
          Save your plan free to unlock all 12 months &rarr;
        </p>
      )}
    </>
  )

  // Bare: chromeless tap target inside the digest card — a tap on any locked
  // row scrolls back up to the save ask at the top of the same card (or the
  // explicit handler). Standalone: a full card doing the same.
  if (bare) {
    return (
      <button
        type="button"
        onClick={handleClick}
        aria-label="Save your plan free to unlock all 12 months"
        className={cn("relative w-full cursor-pointer text-left", className)}
      >
        {inner}
      </button>
    )
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn(
        "relative w-full cursor-pointer text-left rounded-2xl border border-gray-200 bg-white p-4 md:p-5 shadow-sm hover:border-green-200 transition-colors",
        className,
      )}
    >
      {inner}
    </button>
  )
}
